import { useCallback } from "react";
import type { Dispatch, SetStateAction } from "react";
import { applyBlock, type Block, type ComposedBlock, type Draft } from "@jackedlogs/shared";

import {
  applyBlockEditScoped,
  applyWeekEditScoped,
  composeBlocks,
  findSlugConflict,
  reconcileMemberSlots,
  type SlugConflict,
} from "./composeBlocks";
import type { ComposerForm } from "./useComposer";

// The block-commit half of useComposer, split out of it (500-line budget),
// exactly as useRampEdit carries the ramp/per-week machine. Every commit
// still goes through the P1 block layer (applyBlock / applyBlockEditScoped /
// applyWeekEditScoped), whose jest identity guards carry the §3.2 lossless
// round-trip; nothing about the data path changes, only where it lives.
export function useComposerCommit({
  blocks,
  form,
  weekEdit,
  setDraft,
  setForm,
  setFormConflict,
  emptyForm,
  allowDuplicateSlugs,
}: {
  blocks: Block[];
  form: ComposerForm;
  weekEdit: number | null;
  setDraft: Dispatch<SetStateAction<Draft>>;
  setForm: Dispatch<SetStateAction<ComposerForm>>;
  setFormConflict: Dispatch<SetStateAction<SlugConflict | null>>;
  emptyForm: () => ComposerForm;
  /** LG-41 — programs may now hold the same exercise twice on one day.
   *
   *  The guard this skips encoded a decision of Antenore's from 2026-06-11
   *  («vietare due esercizi uguali nello stesso giorno e settimana»); he
   *  reversed it on 2026-08-14, for the same reason LG-39 reversed it inside
   *  a workout. It is safe to drop HERE because a Slot carries its own id
   *  (program.ts:150), so two slots sharing a slug stay distinct rows.
   *
   *  Plans (`mode: "single"`) keep the guard: a PlannedExerciseEntry has no
   *  id, so there a duplicate really would be ambiguous (LG-40). */
  allowDuplicateSlugs: boolean;
}) {
  const canCommit =
    form.members.length > 0 && form.days.size > 0 && form.weeks.size > 0;

  const commit = useCallback(() => {
    const conflict = allowDuplicateSlugs
      ? null
      : findSlugConflict(
          blocks,
          form.editing,
          form.members.map((m) => m.exerciseId),
          [...form.days],
          weekEdit !== null ? [weekEdit] : [...form.weeks],
        );
    // Cleared unconditionally, not only when one is found: a conflict raised
    // before the rule changed would otherwise sit there blocking the commit.
    setFormConflict(conflict);
    if (conflict) return;

    // Per-week commit: narrow the owning segment to its other weeks,
    // apply this week's content separately — the natural "break the
    // segment" gesture. slotIds carry over per member so values edit the
    // SAME slot; a ⇄-swapped member gets "" (a slot has ONE exercise —
    // the divergent week becomes its own slot). CD-1 — day-scoped: `single`
    // targets only the day(s) in form.days (seeded to {editingDay}), and
    // applyWeekEditScoped narrows just those days, so a per-week edit on a
    // cloned day never touches its siblings. Data path jest-locked in
    // composeBlocks.test.ts (§per-week split + §CD-1).
    if (weekEdit !== null && form.editing) {
      const owner = form.editing;
      const members = form.members.map((m) => ({
        ...m,
        setRows: m.setRows.map((r) => ({ ...r })),
      }));
      // Which old member each surviving member continues. Was an index
      // comparison inline here; LG-41 made a block able to hold one slug
      // twice, and index-matching then hands a member the wrong slot (or
      // none). One helper now, shared and tested — see its header for why it
      // is deliberately NOT `updateBlock`'s rule.
      const mapping = reconcileMemberSlots(owner.members, members);
      const single: ComposedBlock = {
        kind: "composed",
        members,
        weeks: [weekEdit],
        placements: owner.placements
          .filter((p) => form.days.has(p.dayIdx))
          .map((p) => ({
            dayIdx: p.dayIdx,
            slotIds: mapping.map((j) => (j >= 0 ? (p.slotIds[j] ?? "") : "")),
            positions: mapping.map((j, i) =>
              j >= 0 ? (p.positions[j] ?? -1) : (p.positions[i] ?? -1),
            ),
            group: p.group,
          })),
      };
      if (single.placements.length === 0) return;
      setDraft((d) => applyWeekEditScoped(d, owner, single));
      setForm(emptyForm());
      return;
    }

    const built = composeBlocks(
      form.members,
      form.grouped,
      [...form.days],
      [...form.weeks],
    );
    if (built.length === 0) return;
    const editing = form.editing;
    const editingDay = form.editingDay;
    setDraft((d) => {
      if (editing) return applyBlockEditScoped(d, editing, built[0]!, editingDay);
      let next = d;
      for (const b of built) next = applyBlock(next, b);
      return next;
    });
    setForm(emptyForm());
  }, [
    blocks,
    form,
    weekEdit,
    setDraft,
    setForm,
    setFormConflict,
    emptyForm,
    allowDuplicateSlugs,
  ]);

  return { canCommit, commit };
}
